import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchRepairById,
  fetchRepairPayments,
  fetchStaffProfiles,
  updateRepairJobSafeRpc,
  removeRepairPartRpc,
  recordRepairCollectionRpc,
  fetchRepairTimeline,
} from '@/services/repairs';
import { fetchShopSettings } from '@/services/settings';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { RepairJob } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { AddRepairPartModal } from './AddRepairPartModal';
import { RepairPaymentModal } from './RepairPaymentModal';
import { useAuth } from '@/context/AuthContext';
import { printRepairJobSheet } from '@/lib/repairJobSheetPrinter';
import {
  Wrench,
  User,
  Phone,
  Printer,
  DollarSign,
  CreditCard,
  Loader2,
  Package,
  Plus,
  Save,
  Trash2,
  Clock,
  ThumbsUp,
  ThumbsDown,
  History,
  CheckCheck,
} from 'lucide-react';

interface RepairDetailProps {
  repairId: string;
  onClose: () => void;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export const RepairDetail: React.FC<RepairDetailProps> = ({ repairId, onClose }) => {
  const { hasPermission } = useAuth();
  const isStaff = !hasPermission('purchases');
  const canViewCosts = hasPermission('purchases');
  const queryClient = useQueryClient();

  const [showAddPartModal, setShowAddPartModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collectionNotes, setCollectionNotes] = useState('');
  const [collectionSignature, setCollectionSignature] = useState('');
  const [removingPartId, setRemovingPartId] = useState<string | null>(null);

  // Queries
  const { data: repair, isLoading, error, refetch } = useQuery<RepairJob>({
    queryKey: queryKeys.repairs.detail(repairId),
    queryFn: () => fetchRepairById(repairId, isStaff),
  });

  const { data: payments = [] } = useQuery({
    queryKey: queryKeys.repairs.payments(repairId),
    queryFn: () => fetchRepairPayments(repairId),
    enabled: !!repair,
  });

  const { data: staffList = [] } = useQuery({
    queryKey: ['staff-profiles'],
    queryFn: () => fetchStaffProfiles(),
  });

  const { data: timeline = [] } = useQuery({
    queryKey: ['repair-timeline', repairId],
    queryFn: () => fetchRepairTimeline(repairId),
    enabled: !!repairId,
  });

  const { data: shopSettings } = useQuery({
    queryKey: ['shop-settings'],
    queryFn: fetchShopSettings,
  });

  // Edit Job Form State
  const [editStatus, setEditStatus] = useState<string>('');
  const [editPriority, setEditPriority] = useState<string>('');
  const [editDiagnosis, setEditDiagnosis] = useState<string>('');
  const [editWorkPerformed, setEditWorkPerformed] = useState<string>('');
  const [editLabourCharge, setEditLabourCharge] = useState<number>(0);
  const [editAssignedTo, setEditAssignedTo] = useState<string>('');
  const [editEstimatedCompletion, setEditEstimatedCompletion] = useState<string>('');
  const [editApprovalStatus, setEditApprovalStatus] = useState<string>('pending');
  const [editApprovalNotes, setEditApprovalNotes] = useState<string>('');
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const initEditForm = (r: RepairJob) => {
    setEditStatus(r.status);
    setEditPriority(r.priority);
    setEditDiagnosis(r.diagnosis ?? '');
    setEditWorkPerformed(r.work_performed ?? '');
    setEditLabourCharge(r.labour_charge ?? 0);
    setEditAssignedTo(r.assigned_to ?? '');
    setEditEstimatedCompletion(r.estimated_completion ?? '');
    setEditApprovalStatus(r.customer_approval_status ?? 'pending');
    setEditApprovalNotes(r.approval_notes ?? '');
    setIsEditing(true);
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      await updateRepairJobSafeRpc({
        repair_id: repairId,
        status: editStatus,
        priority: editPriority,
        diagnosis: editDiagnosis || null,
        work_performed: editWorkPerformed || null,
        labour_charge: editLabourCharge,
        assigned_to: editAssignedTo || null,
        estimated_completion: editEstimatedCompletion || null,
        customer_approval_status: editApprovalStatus || null,
        approval_notes: editApprovalNotes || null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.detail(repairId) });
      void queryClient.invalidateQueries({ queryKey: ['repair-timeline', repairId] });
      setIsEditing(false);
    },
    onError: (err: unknown) => {
      setUpdateError(parseDatabaseError(err));
    },
  });

  const removePartMutation = useMutation({
    mutationFn: (partId: string) => removeRepairPartRpc(partId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.detail(repairId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      void queryClient.invalidateQueries({ queryKey: ['repair-timeline', repairId] });
    },
    onError: (err: unknown) => {
      setUpdateError(parseDatabaseError(err));
    },
    onSettled: () => setRemovingPartId(null),
  });

  const collectionMutation = useMutation({
    mutationFn: () =>
      recordRepairCollectionRpc({
        repair_id: repairId,
        collection_notes: collectionNotes.trim() || null,
        signature: collectionSignature.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.detail(repairId) });
      void queryClient.invalidateQueries({ queryKey: ['repair-timeline', repairId] });
      setShowCollectionModal(false);
      setCollectionNotes('');
      setCollectionSignature('');
    },
    onError: (err: unknown) => {
      setUpdateError(parseDatabaseError(err));
    },
  });

  const handleSaveJob = (e: React.FormEvent) => {
    e.preventDefault();
    setUpdateError(null);
    updateMutation.mutate();
  };

  const handleRemovePart = (partId: string) => {
    if (!window.confirm('Are you sure you want to remove this part from the repair?')) return;
    setRemovingPartId(partId);
    removePartMutation.mutate(partId);
  };

  const handlePrintJobSheet = () => {
    if (!repair) return;
    const settings = shopSettings || {
      shop_name: 'AL KUWAIT TECH',
      slogan: 'Laptops & IT Solutions',
      address: '',
      phone: '',
      city: '',
    };
    printRepairJobSheet({ shop: settings, repair });
  };

  const balanceDue = repair ? Math.max(0, (repair.balance_due ?? (repair.total_charge - repair.amount_paid))) : 0;
  const device = repair?.customer_device || repair?.inventory_laptop;

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {repair && !isLoading && (
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1 text-xs">
          {updateError && <ErrorAlert message={updateError} />}

          {/* Ticket Header */}
          <div className="print:p-6 print:bg-white print:text-black space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2 pb-3 border-b border-brand-border print:border-black">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-bold text-brand-black print:text-xl">
                    REPAIR TICKET #{repair.job_number}
                  </h3>
                  <span
                    className={`px-2 py-0.5 text-xs font-bold rounded-full uppercase print:border print:border-black ${
                      repair.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : repair.status === 'returned'
                        ? 'bg-blue-100 text-blue-800'
                        : repair.status === 'cancelled' || repair.status === 'cannot_repair'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {repair.status.replace('_', ' ')}
                  </span>
                  <span
                    className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${
                      repair.priority === 'urgent'
                        ? 'bg-red-600 text-white'
                        : repair.priority === 'high'
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-200 text-gray-800'
                    }`}
                  >
                    {repair.priority} Priority
                  </span>
                </div>
                <p className="text-xs text-brand-muted mt-0.5 print:text-black">
                  Received: <strong>{formatDate(repair.received_at)}</strong>
                  {repair.estimated_completion && (
                    <span> • Est. Completion: <strong>{formatDate(repair.estimated_completion)}</strong></span>
                  )}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 print:hidden">
                <button
                  type="button"
                  onClick={handlePrintJobSheet}
                  className="inline-flex items-center space-x-1 px-3 py-1.5 bg-brand-bg text-brand-black font-semibold rounded border border-brand-border hover:bg-white"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Print Job Sheet</span>
                </button>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={() => initEditForm(repair)}
                    className="inline-flex items-center space-x-1 px-3 py-1.5 bg-brand-green text-white font-semibold rounded hover:bg-brand-green-hover"
                  >
                    <Wrench className="w-3.5 h-3.5" />
                    <span>Update Job</span>
                  </button>
                )}
                {!repair.collected_at && (
                  <button
                    type="button"
                    onClick={() => setShowCollectionModal(true)}
                    className="inline-flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    <span>Device Collected</span>
                  </button>
                )}
              </div>
            </div>

            {/* Collection Banner (if collected) */}
            {repair.collected_at && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between text-blue-900">
                <div className="flex items-center space-x-2">
                  <CheckCheck className="w-4 h-4 text-blue-600 shrink-0" />
                  <div>
                    <p className="font-bold">Device Collected on {formatDateTime(repair.collected_at)}</p>
                    {repair.collection_notes && <p className="text-[11px] text-blue-700">{repair.collection_notes}</p>}
                  </div>
                </div>
                {repair.collection_signature && (
                  <span className="text-[10px] font-mono bg-white px-2 py-0.5 rounded border text-blue-800">
                    Sig: {repair.collection_signature}
                  </span>
                )}
              </div>
            )}

            {/* Customer & Device Information Header */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-brand-bg print:bg-gray-50 p-3 rounded-lg border border-brand-border print:border-black">
              <div>
                <p className="font-bold text-xs text-brand-black uppercase mb-1">Customer Information</p>
                <p className="font-semibold text-brand-black flex items-center space-x-1">
                  <User className="w-3.5 h-3.5 text-brand-green print:hidden" />
                  <span>{repair.customer?.name}</span>
                </p>
                {repair.customer?.phone && (
                  <p className="text-brand-muted print:text-black flex items-center space-x-1 mt-0.5">
                    <Phone className="w-3.5 h-3.5 text-brand-green print:hidden" />
                    <span>{repair.customer.phone}</span>
                  </p>
                )}
              </div>

              <div>
                <p className="font-bold text-xs text-brand-black uppercase mb-1">Device Details</p>
                <p className="font-semibold text-brand-black">
                  {device ? `${device.brand || ''} ${device.model || ''}` : 'Device'}
                  {device?.serial_number ? ` (S/N: ${device.serial_number})` : ''}
                </p>
                {repair.customer_device && (
                  <div className="text-[11px] text-brand-muted print:text-black mt-0.5 space-y-0.5">
                    <p>
                      {repair.customer_device.cpu ? `CPU: ${repair.customer_device.cpu} • ` : ''}
                      {repair.customer_device.ram ? `RAM: ${repair.customer_device.ram} • ` : ''}
                      {repair.customer_device.storage ? `Storage: ${repair.customer_device.storage}` : ''}
                    </p>
                    <p>
                      Charger Received:{' '}
                      <strong className="text-brand-black">
                        {repair.customer_device.charger_received ? 'YES' : 'NO'}
                      </strong>
                    </p>
                  </div>
                )}
                {repair.accessories_received && repair.accessories_received.length > 0 && (
                  <p className="text-[11px] text-brand-muted mt-0.5">
                    Accessories: <strong className="text-brand-black">{repair.accessories_received.join(', ')}</strong>
                  </p>
                )}
              </div>
            </div>

            {/* Customer Approval Card */}
            <div className="p-3 rounded-lg border bg-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="flex items-center space-x-2">
                {repair.customer_approval_status === 'approved' ? (
                  <ThumbsUp className="w-4 h-4 text-green-600 shrink-0" />
                ) : repair.customer_approval_status === 'declined' ? (
                  <ThumbsDown className="w-4 h-4 text-red-600 shrink-0" />
                ) : (
                  <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                )}
                <div>
                  <span className="font-bold text-brand-black">Customer Approval: </span>
                  <span
                    className={`font-extrabold uppercase px-1.5 py-0.5 rounded text-[10px] ${
                      repair.customer_approval_status === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : repair.customer_approval_status === 'declined'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {repair.customer_approval_status || 'pending'}
                  </span>
                  {repair.approval_date && (
                    <span className="text-brand-muted ml-1">on {formatDate(repair.approval_date)}</span>
                  )}
                  {repair.approval_notes && (
                    <p className="text-[11px] text-brand-muted mt-0.5 italic">"{repair.approval_notes}"</p>
                  )}
                </div>
              </div>

              {(repair.estimated_labour || repair.estimated_parts || repair.estimated_charge) && (
                <div className="text-right text-[11px] text-brand-muted shrink-0">
                  <span>Est. Labour: {formatCurrency(repair.estimated_labour)}</span> •{' '}
                  <span>Est. Parts: {formatCurrency(repair.estimated_parts)}</span> •{' '}
                  <span className="font-bold text-brand-black">Total Est: {formatCurrency(repair.estimated_charge)}</span>
                </div>
              )}
            </div>

            {/* Reported Issue & Diagnosis */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-red-50/50 rounded border border-red-200 print:border-black">
                <p className="font-bold text-red-900 print:text-black">Reported Issue</p>
                <p className="text-red-800 print:text-black mt-0.5">{repair.reported_issue}</p>
              </div>

              <div className="p-3 bg-blue-50/50 rounded border border-blue-200 print:border-black">
                <p className="font-bold text-blue-900 print:text-black">Technical Diagnosis</p>
                <p className="text-blue-800 print:text-black mt-0.5">{repair.diagnosis || 'Pending diagnosis...'}</p>
              </div>
            </div>

            {repair.work_performed && (
              <div className="p-3 bg-green-50/50 rounded border border-green-200 print:border-black">
                <p className="font-bold text-green-900 print:text-black">Work Performed</p>
                <p className="text-green-800 print:text-black mt-0.5">{repair.work_performed}</p>
              </div>
            )}

            {/* Inline Job Update Form */}
            {isEditing && (
              <form onSubmit={handleSaveJob} className="p-3 bg-white border-2 border-brand-green rounded-lg space-y-3 print:hidden">
                <h4 className="font-bold text-brand-black uppercase">Update Repair Status & Charges</h4>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <label className="block font-bold uppercase mb-1">Status</label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full px-2 py-1 border rounded font-semibold text-brand-black"
                    >
                      <option value="received">Received</option>
                      <option value="diagnosing">Diagnosing</option>
                      <option value="waiting_approval">Waiting Approval</option>
                      <option value="in_progress">In Progress</option>
                      <option value="waiting_parts">Waiting Parts</option>
                      <option value="completed">Completed</option>
                      <option value="returned">Returned / Delivered</option>
                      <option value="cannot_repair">Cannot Repair</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-bold uppercase mb-1">Priority</label>
                    <select
                      value={editPriority}
                      onChange={(e) => setEditPriority(e.target.value)}
                      className="w-full px-2 py-1 border rounded"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-bold uppercase mb-1">Labour Charge (PKR)</label>
                    <input
                      type="number"
                      min={0}
                      value={editLabourCharge}
                      onChange={(e) => setEditLabourCharge(Number(e.target.value) || 0)}
                      className="w-full px-2 py-1 border rounded font-bold text-brand-green"
                    />
                  </div>

                  <div>
                    <label className="block font-bold uppercase mb-1">Assigned Staff</label>
                    <select
                      value={editAssignedTo}
                      onChange={(e) => setEditAssignedTo(e.target.value)}
                      className="w-full px-2 py-1 border rounded"
                    >
                      <option value="">Unassigned</option>
                      {staffList.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Approval update */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border-t pt-2">
                  <div>
                    <label className="block font-bold uppercase mb-1">Customer Approval</label>
                    <select
                      value={editApprovalStatus}
                      onChange={(e) => setEditApprovalStatus(e.target.value)}
                      className="w-full px-2 py-1 border rounded font-semibold"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="declined">Declined</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-bold uppercase mb-1">Approval Notes</label>
                    <input
                      type="text"
                      value={editApprovalNotes}
                      onChange={(e) => setEditApprovalNotes(e.target.value)}
                      placeholder="e.g. Customer approved via phone call..."
                      className="w-full px-2 py-1 border rounded"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block font-semibold mb-1">Diagnosis Notes</label>
                    <textarea
                      rows={2}
                      value={editDiagnosis}
                      onChange={(e) => setEditDiagnosis(e.target.value)}
                      className="w-full px-2 py-1 border rounded resize-none"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold mb-1">Work Performed Notes</label>
                    <textarea
                      rows={2}
                      value={editWorkPerformed}
                      onChange={(e) => setEditWorkPerformed(e.target.value)}
                      className="w-full px-2 py-1 border rounded resize-none"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-2 pt-1 border-t">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-3 py-1 bg-brand-bg border font-semibold rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center space-x-1 px-4 py-1 bg-brand-green text-white font-bold rounded hover:bg-brand-green-hover"
                  >
                    {updateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    <span>Save Changes</span>
                  </button>
                </div>
              </form>
            )}

            {/* Repair Parts Table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-brand-black uppercase tracking-wider">
                  Parts & Materials Used ({repair.parts?.length ?? 0})
                </h4>
                <button
                  type="button"
                  onClick={() => setShowAddPartModal(true)}
                  className="inline-flex items-center space-x-1 text-brand-green font-semibold hover:underline print:hidden"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>+ Add Repair Part</span>
                </button>
              </div>

              <div className="bg-white border border-brand-border print:border-black rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-brand-bg print:bg-gray-100 border-b border-brand-border print:border-black font-semibold text-brand-muted print:text-black">
                    <tr>
                      <th className="text-left px-3 py-2">Part Description</th>
                      <th className="text-center px-3 py-2">Qty</th>
                      {canViewCosts && <th className="text-right px-3 py-2 print:hidden">Unit Cost</th>}
                      <th className="text-right px-3 py-2">Unit Charge</th>
                      <th className="text-right px-3 py-2">Line Charge</th>
                      <th className="px-2 py-2 print:hidden"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border print:divide-black">
                    {repair.parts?.length === 0 ? (
                      <tr>
                        <td colSpan={canViewCosts ? 6 : 5} className="p-3 text-center text-brand-muted italic">
                          No repair parts added yet.
                        </td>
                      </tr>
                    ) : (
                      repair.parts?.map((part) => (
                        <tr key={part.id}>
                          <td className="px-3 py-2 font-semibold text-brand-black">
                            <span className="flex items-center space-x-1">
                              <Package className="w-3.5 h-3.5 text-blue-600 print:hidden shrink-0" />
                              <span>{part.description}</span>
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center font-bold">{part.quantity}</td>
                          {canViewCosts && (
                            <td className="px-3 py-2 text-right text-brand-muted print:hidden">
                              {formatCurrency(part.unit_cost)}
                            </td>
                          )}
                          <td className="px-3 py-2 text-right">{formatCurrency(part.unit_charge)}</td>
                          <td className="px-3 py-2 text-right font-bold text-brand-black">
                            {formatCurrency(part.quantity * part.unit_charge)}
                          </td>
                          <td className="px-2 py-2 text-center print:hidden">
                            <button
                              type="button"
                              onClick={() => handleRemovePart(part.id)}
                              disabled={removingPartId === part.id}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Remove part"
                            >
                              {removingPartId === part.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Financial Totals */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                {repair.intake_notes && (
                  <div className="p-3 bg-brand-bg print:bg-gray-50 rounded border border-brand-border print:border-black">
                    <p className="font-bold text-brand-black">Intake Notes</p>
                    <p className="text-brand-muted print:text-black">{repair.intake_notes}</p>
                  </div>
                )}

                {canViewCosts && (
                  <div className="p-3 bg-green-50/60 rounded border border-green-200 text-green-900 print:hidden">
                    <p className="font-bold uppercase tracking-wider text-[10px]">Owner Cost & Margin Breakdown</p>
                    <div className="flex justify-between mt-1 font-semibold">
                      <span>Parts Cost: {formatCurrency(repair.parts_cost)}</span>
                      <span>
                        Net Margin: {formatCurrency(repair.total_charge - repair.parts_cost)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-brand-bg print:bg-gray-50 p-3 rounded-lg border border-brand-border print:border-black space-y-1.5">
                <div className="flex justify-between font-semibold">
                  <span>Parts Charge:</span>
                  <span>{formatCurrency(repair.parts_charge)}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Labour Charge:</span>
                  <span>{formatCurrency(repair.labour_charge)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold text-brand-black border-t pt-1 border-brand-border print:border-black">
                  <span>Total Repair Charge:</span>
                  <span className="text-brand-green">{formatCurrency(repair.total_charge)}</span>
                </div>
                <div className="flex justify-between text-brand-muted print:text-black">
                  <span>Amount Paid:</span>
                  <span className="text-brand-green font-semibold">{formatCurrency(repair.amount_paid)}</span>
                </div>
                <div className="flex justify-between text-sm font-bold border-t pt-1 border-brand-border print:border-black">
                  <span>Balance Due:</span>
                  <span className={balanceDue > 0 ? 'text-brand-red' : 'text-brand-green'}>
                    {formatCurrency(balanceDue)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment History */}
            {payments.length > 0 && (
              <div className="pt-2 border-t border-brand-border print:border-black">
                <h4 className="font-bold text-brand-black uppercase tracking-wider mb-2 flex items-center space-x-1">
                  <CreditCard className="w-3.5 h-3.5 text-brand-green print:hidden" />
                  <span>Payment History</span>
                </h4>
                <div className="space-y-1.5">
                  {payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-2 bg-white print:bg-gray-50 rounded border border-brand-border print:border-black">
                      <div>
                        <span className="font-semibold text-brand-green">{formatCurrency(p.amount)}</span>
                        <span className="text-brand-muted print:text-black"> via {p.payment_method}</span>
                        {p.reference && <span className="text-brand-muted print:text-black"> (Ref: {p.reference})</span>}
                        {p.notes && <p className="text-[10px] text-brand-muted print:text-black">{p.notes}</p>}
                      </div>
                      <span className="text-brand-muted print:text-black">{formatDate(p.payment_date || p.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Timeline */}
            {timeline.length > 0 && (
              <div className="pt-2 border-t border-brand-border print:hidden">
                <h4 className="font-bold text-brand-black uppercase tracking-wider mb-2 flex items-center space-x-1">
                  <History className="w-3.5 h-3.5 text-brand-green" />
                  <span>Activity Timeline ({timeline.length})</span>
                </h4>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {timeline.map((entry: any) => (
                    <div key={entry.id} className="p-2 bg-brand-bg rounded border border-brand-border flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-brand-black">{entry.description}</p>
                        <p className="text-[10px] text-brand-muted">
                          by {entry.profile?.full_name || 'System'}
                          {entry.action_type && ` • [${entry.action_type}]`}
                        </p>
                      </div>
                      <span className="text-[10px] text-brand-muted shrink-0 ml-2">
                        {formatDateTime(entry.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Modals */}
          {showAddPartModal && (
            <AddRepairPartModal repairId={repair.id} isOpen={showAddPartModal} onClose={() => setShowAddPartModal(false)} />
          )}

          {showPaymentModal && (
            <RepairPaymentModal repair={repair} isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} />
          )}

          {/* Device Collection Modal */}
          {showCollectionModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-4 space-y-4 text-xs">
                <h3 className="text-sm font-bold text-brand-black flex items-center space-x-1.5">
                  <CheckCheck className="w-4 h-4 text-blue-600" />
                  <span>Record Device Collection</span>
                </h3>
                <p className="text-brand-muted">
                  Confirm customer is picking up device #{repair.job_number}.
                </p>

                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Collection Notes (Optional)</label>
                  <textarea
                    rows={2}
                    value={collectionNotes}
                    onChange={(e) => setCollectionNotes(e.target.value)}
                    placeholder="e.g. Tested in front of customer, handed over power adapter..."
                    className="w-full px-2.5 py-1.5 border rounded resize-none"
                  />
                </div>

                <div>
                  <label className="block font-bold text-brand-black uppercase mb-1">Customer Signature / Receiver Name</label>
                  <input
                    type="text"
                    value={collectionSignature}
                    onChange={(e) => setCollectionSignature(e.target.value)}
                    placeholder="e.g. Muhammad Ali"
                    className="w-full px-2.5 py-1.5 border rounded"
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-2 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCollectionModal(false)}
                    disabled={collectionMutation.isPending}
                    className="px-3 py-1.5 font-semibold text-brand-muted hover:text-brand-black"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => collectionMutation.mutate()}
                    disabled={collectionMutation.isPending}
                    className="inline-flex items-center space-x-1 px-4 py-1.5 bg-blue-600 text-white font-bold rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {collectionMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span>Confirm Collection</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-brand-border print:hidden">
        {repair && balanceDue > 0 ? (
          <button
            type="button"
            onClick={() => setShowPaymentModal(true)}
            className="inline-flex items-center space-x-1.5 px-4 py-2 bg-brand-green text-white text-xs font-bold rounded shadow-sm hover:bg-brand-green-hover"
          >
            <DollarSign className="w-4 h-4" />
            <span>Record Payment</span>
          </button>
        ) : (
          <div />
        )}

        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black">
          Close
        </button>
      </div>
    </>
  );
};
